import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useLocalization } from "@/context/LocalizationContext";

const Settings = () => {
  const location = useLocation();
  const { t } = useLocalization();
  const [searchParams, setSearchParams] = useSearchParams();
  const [callbackNotice, setCallbackNotice] = useState(null);
  const breadcrumbLabel = location.pathname.startsWith("/settings/imports")
    ? t("settings.breadcrumb.imports")
    : location.pathname.startsWith("/settings/notifications")
      ? t("settings.breadcrumb.notifications")
      : location.pathname.startsWith("/settings/guide")
        ? t("settings.breadcrumb.guide")
        : t("settings.breadcrumb.root");

  useEffect(() => {
    const status = searchParams.get("mail_status");
    if (!status) {
      return;
    }

    const provider = searchParams.get("mail_provider");
    const message = searchParams.get("mail_message");
    const providerLabel =
      provider === "google"
        ? t("common.providers.google")
        : provider === "microsoft"
          ? t("common.providers.microsoft")
          : null;
    const fallbackMessage =
      status === "connected"
        ? t("settings.callback.connected", { provider: providerLabel })
        : t("settings.callback.failed");
    const resolvedMessage =
      status === "connected" ? fallbackMessage : message || fallbackMessage;

    if (status === "connected") {
      toast.success(resolvedMessage);
    } else {
      toast.error(resolvedMessage);
    }

    setCallbackNotice({
      status,
      provider,
      message: resolvedMessage,
    });

    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, t]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="mb-2 uppercase text-xs tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400">
            {breadcrumbLabel}
          </p>
        </div>
      </div>

      {callbackNotice && (
        <div
          className={`rounded-none border px-4 py-4 text-sm sm:px-5 ${
            callbackNotice.status === "connected"
              ? "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/70 dark:bg-emerald-950/40 dark:text-emerald-300"
              : "border-red-200 bg-red-50 text-red-800 dark:border-red-900/70 dark:bg-red-950/40 dark:text-red-300"
          }`}
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="uppercase text-xs tracking-[0.2em] font-medium">
                {callbackNotice.provider === "google"
                  ? `${t("common.providers.google")} ${t("common.status")}`
                  : callbackNotice.provider === "microsoft"
                    ? `${t("common.providers.microsoft")} ${t("common.status")}`
                    : t("settings.callback.connectionStatus")}
              </p>
              <p className="mt-1">{callbackNotice.message}</p>
            </div>
            <button
              data-testid="dismiss-callback-notice"
              onClick={() => setCallbackNotice(null)}
              className="self-start border border-current/20 px-3 py-1 text-xs font-medium uppercase tracking-[0.12em] hover:opacity-80"
            >
              {t("common.dismiss")}
            </button>
          </div>
        </div>
      )}

      <Outlet />
    </div>
  );
};

export default Settings;
